import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import PopupInputFieldforObservationView from "../PopupInputFieldforObservationView";
import "../../.csscode/Table.css";
import { toast } from "sonner";
import CustomLoader from "../static/CustomLoader";
import { FaListUl, FaTimes } from "react-icons/fa";
import {
  uploadDocument,
  viewDocument,
  deleteDocument,
} from "../utiles/DocumentService";

/* ============================================================
   STYLES — scoped to the observation detail table layout
   ============================================================ */
const odStyles = `
.od-wrap { width: 100%; }

/* ---- Toggle button ---- */
.od-toggle {
  display: inline-flex; align-items: center; gap: 8px;
  height: 40px; padding: 0 18px; margin-bottom: 16px;
  border-radius: 8px; font-size: 14px; font-weight: 600;
  border: 1px solid transparent; cursor: pointer; user-select: none;
  transition: background-color .2s, box-shadow .2s, border-color .2s, transform .1s;
}
.od-toggle:active { transform: translateY(1px); }
.od-toggle--open  { background: #4f46e5; color: #fff; box-shadow: 0 2px 6px rgba(79,70,229,.3); }
.od-toggle--open:hover  { background: #4338ca; }
.od-toggle--close { background: #f59e0b; color: #fff; box-shadow: 0 2px 6px rgba(245,158,11,.3); }
.od-toggle--close:hover { background: #d97706; }

/* ---- Chapter card ---- */
.od-chapter {
  margin-bottom: 24px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15,23,42,.05);
}
.od-chapter-header {
  background: linear-gradient(90deg, #1e293b 0%, #334155 100%);
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: .2px;
  padding: 10px 16px;
}

/* ---- Question table (4-column layout) ----
   Auto layout so the labels hug their content and
   the value cells absorb all remaining space. */
.od-q-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  background: #fff;
  table-layout: auto;
}
.od-q-table + .od-q-table { border-top: 2px solid #e2e8f0; }
.od-q-table td {
  border: 1px solid #cbd5e1;
  padding: 10px 12px;
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}

/* Question title row spans all columns */
.od-q-table .od-q-header {
  background: #f1f5f9;
  color: #1e293b;
  font-weight: 700;
  font-size: 14px;
}

/* Label cells — hug content, never stretch */
.od-q-table .od-label {
  width: 1px;               /* trick: shrink to minimum */
  white-space: nowrap;      /* keep the label on one line */
  background: #f8fafc;
  font-weight: 600;
  color: #1e293b;
  font-size: 13px;
}

/* Value cells — take all remaining width */
.od-q-table .od-value { background: #fff; }

.od-q-header-cell { display: flex; align-items: center; gap: 10px; }
.od-serial {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 26px; height: 26px; padding: 0 8px;
  border-radius: 6px;
  background: #1e293b; color: #fff;
  font-size: 12px; font-weight: 700;
  flex: 0 0 auto;
}

/* ---- Image grid ---- */
.od-img-grid {
  display: flex; flex-wrap: wrap; gap: 12px;
  margin-top: 10px; padding: 10px;
  border: 1px dashed #cbd5e1; border-radius: 8px;
  background: #f8fafc;
}
.od-img-card {
  position: relative;
  width: 96px; height: 116px;
  border: 1px solid #e2e8f0; border-radius: 8px;
  background: #fff;
  display: flex; flex-direction: column; align-items: center;
  padding: 6px;
  box-shadow: 0 1px 3px rgba(15,23,42,.06);
}
.od-img-card img {
  width: 100%; height: 68px;
  object-fit: cover; border-radius: 6px;
  cursor: pointer;
  background: #e2e8f0;
  color: #64748b;
  font-size: 11px;
}
.od-img-card .od-img-del {
  position: absolute; top: 5px; right: 5px;
  width: 22px; height: 22px;
  border-radius: 50%;
  background: #fee2e2; color: #b91c1c;
  border: none;
  font-size: 14px; font-weight: bold;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,0,0,.1);
}
.od-img-card .od-img-del:hover { background: #fecaca; }
.od-img-card .od-img-name {
  font-size: 10px; color: #475569;
  text-align: center;
  margin-top: 6px;
  width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* ---- Empty ---- */
.od-empty {
  padding: 16px; text-align: center;
  color: #64748b;
  border: 1px dashed #cbd5e1; border-radius: 8px;
  background: #f8fafc;
}

/* ---- Chapter drawer floating button ---- */
.od-fab {
  position: fixed; top: 190px; right: 20px; z-index: 2002;
  width: 50px; height: 50px; border-radius: 50%;
  background: #4f46e5; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; border: none; cursor: pointer;
  box-shadow: 0 4px 10px rgba(15,23,42,.25);
  transition: transform .15s, background-color .2s, box-shadow .2s;
}
.od-fab:hover { transform: translateY(-2px); background: #4338ca; box-shadow: 0 6px 16px rgba(15,23,42,.3); }

.od-drawer {
  position: fixed; top: 0; height: 100%; width: 280px;
  background: #fff;
  box-shadow: 0 0 20px rgba(0,0,0,.2);
  transition: right .3s ease-in-out;
  z-index: 2001;
  display: flex; flex-direction: column;
}
.od-drawer-head {
  background: linear-gradient(90deg, #1e293b 0%, #334155 100%);
  color: #fff;
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; font-weight: 700;
}
.od-drawer-head svg { cursor: pointer; font-size: 18px; }
.od-drawer-search { padding: 10px; border-bottom: 1px solid #eee; }
.od-drawer-search input {
  width: 100%; padding: 8px; border-radius: 6px;
  border: 1px solid #cbd5e1; font-size: 13px; outline: none;
}
.od-drawer-search input:focus { border-color: #4f46e5; box-shadow: 0 0 0 3px rgba(79,70,229,.15); }
.od-drawer-list { overflow-y: auto; flex: 1; }
.od-drawer-item {
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13.5px;
  transition: background .15s;
}
.od-drawer-item:hover { background: #f8fafc; }
.od-drawer-empty { padding: 12px; color: #94a3b8; text-align: center; font-size: 13px; }

/* ---- Full preview overlay ---- */
.od-preview-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.8);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; cursor: pointer;
}
.od-preview-overlay img {
  max-width: 90%; max-height: 90%;
  border-radius: 12px;
}

/* ---- Responsive ---- */
@media (max-width: 900px) {
  .od-q-table { font-size: 13px; }
  .od-q-table .od-label { font-size: 12.5px; }
}
@media (max-width: 600px) {
  .od-q-table { font-size: 12.5px; }
  .od-q-table td { padding: 8px 8px; }
  .od-q-table .od-label { white-space: normal; }  /* allow wrap on tiny screens */
}
`;

const ObservationDetails = ({
  selectedDatacode,
  chapterList,
  questionList,
  divcodeList,
  statusOptions,
  annextureList,
  details,
  setDetails,
  handlesubmit,
  mode,
}) => {
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [showChapters, setShowChapters] = useState(false);
  const [showChapterMenu, setShowChapterMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [previewImg, setPreviewImg] = useState(null);
  const orgcode = localStorage.getItem("custCode") || "ptw";
  const envCode = localStorage.getItem("envcode") || "Prod";
  const apiUrl = localStorage.getItem("apiUrl");
  const chapterRefs = useRef({});

  const [loading, setLoading] = useState(false);

  const filteredChapters = chapterList.filter((chapter) =>
    `${chapter.label} ${chapter.label2}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  /* ---------- image fetch & preview ---------- */
  const fetchAndPreviewImage = async (filename) => {
    const folderPath = `${orgcode}/sc_${selectedDatacode}`;
    setLoading(true);
    try {
      const response = await viewDocument({
        projectCode: "Ptw",
        envCode,
        folderPath,
        fileName: filename,
      });
      if (response.cd === 1) return "";

      const base64Data = response;
      const mimeType = filename.toLowerCase().endsWith(".pdf")
        ? "application/pdf"
        : "image/*";

      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });
      setPreviewImg(URL.createObjectURL(blob));
    } catch (err) {
      toast.error("Failed to load image.");
      console.error("Preview error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAndPreviewResponseImage = async (filename, dep) => {
    const folderPath = `/${orgcode}/sc_${selectedDatacode}/dep_${dep}`;
    setLoading(true);
    try {
      const response = await viewDocument({
        projectCode: "Ptw",
        envCode,
        folderPath,
        fileName: filename,
      });
      if (response.cd === 1) return "";

      const base64Data = response;
      const mimeType = filename.toLowerCase().endsWith(".pdf")
        ? "application/pdf"
        : "image/*";

      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });
      setPreviewImg(URL.createObjectURL(blob));
    } catch (err) {
      toast.error("Failed to load image.");
      console.error("Preview error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (details && details.length > 0) {
      const uniqueChapters = [...new Set(details.map((d) => d.chapter))];
      setSelectedChapters(uniqueChapters);
    } else {
      setSelectedChapters([]);
      setShowChapters(false);
    }
  }, [chapterList]);

  useEffect(() => {
    if (details && details.length > 0) {
      const uniqueChapters = [...new Set(details.map((d) => d.chapter))];
      setSelectedChapters(uniqueChapters);
    } else {
      setSelectedChapters([]);
      setShowChapters(false);
    }
  }, [details]);

  /* ---------- row / chapter handlers (unchanged logic) ---------- */
  const handleAddRow = (chapterCode) => {
    const chapterQuestions = questionList.filter(
      (q) => q.chaptercode === chapterCode,
    );
    const existingQuestions = details
      .filter((d) => d.chapter === chapterCode)
      .map((d) => d.questionId);

    if (chapterCode === 1) {
      const alreadyAdded = selectedChapters.includes(chapterCode);
      if (alreadyAdded) {
        alert("This chapter is already added.");
        return;
      }
      const maxId = Math.max(...details.map((d) => d.questionId || 0), 0);
      const newQuestionId = maxId + 1;
      setDetails((prev) => [
        ...prev,
        {
          chapter: chapterCode,
          question: "",
          questionId: newQuestionId,
          parent: 0,
          observation: "",
          suggestion: "",
          image: [],
          department: "",
          startdate: today,
          enddate: today,
          status: "",
          chapterStatus: 1,
          annexure: "",
          response: "",
          responseImages: [],
          remark: JSON.stringify({
            chapter: chapterCode,
            question: "",
            observation: "",
            image: [],
          }),
        },
      ]);
      setSelectedChapters((prev) => [...prev, chapterCode]);
      return;
    }

    const remainingQuestionObjs = chapterQuestions.filter(
      (q) => !existingQuestions.includes(q.value),
    );

    if (selectedChapters.includes(chapterCode)) {
      if (remainingQuestionObjs.length === 0) {
        alert("This chapter is already added.");
        return;
      } else {
        const confirmAdd = window.confirm(
          "This chapter is already partially added. Do you want to add the remaining questions?",
        );
        if (!confirmAdd) return;
      }
    }

    const allRows = [];
    const mainQuestions = remainingQuestionObjs.filter(
      (q) => !q.parent || q.parent === 0,
    );
    mainQuestions.forEach((mq) => {
      allRows.push({
        chapter: chapterCode,
        question: mq.label,
        questionId: mq.value,
        parent: 0,
        observation: "",
        suggestion: "",
        image: [],
        department: "",
        startdate: today,
        enddate: today,
        status: 1,
        chapterStatus: 1,
        annexure: "",
        response: "",
        responseImages: [],
        remark: JSON.stringify({
          chapter: chapterCode,
          question: mq.label,
          observation: "",
          image: [],
        }),
      });

      const subQuestions = questionList.filter(
        (sq) => sq.parent === mq.value && !existingQuestions.includes(sq.value),
      );
      subQuestions.forEach((sq) => {
        allRows.push({
          chapter: chapterCode,
          question: sq.label,
          questionId: sq.value,
          parent: sq.parent,
          observation: "",
          suggestion: "",
          image: [],
          department: "",
          startdate: today,
          enddate: today,
          status: 1,
          chapterStatus: 1,
          annexure: "",
          response: "",
          responseImages: [],
          remark: JSON.stringify({
            chapter: chapterCode,
            question: sq.label,
            observation: "",
            image: [],
          }),
        });
      });
    });

    setDetails((prev) => [...prev, ...allRows]);
    if (!selectedChapters.includes(chapterCode)) {
      setSelectedChapters((prev) => [...prev, chapterCode]);
    }
  };

  const handleAddFreeRow = (chapterCode) => {
    const maxId = Math.max(...details.map((d) => d.questionId || 0), 0);
    const newQuestionId = maxId + 1;
    const newRow = {
      chapter: chapterCode,
      question: "",
      questionId: newQuestionId,
      parent: 0,
      observation: "",
      suggestion: "",
      image: [],
      department: "",
      startdate: today,
      enddate: today,
      status: 1,
      chapterStatus: 1,
      annexure: "",
      response: "",
      responseImages: [],
      remark: JSON.stringify({
        chapter: chapterCode,
        question: "",
        observation: "",
        image: [],
      }),
    };
    setDetails((prev) => [...prev, newRow]);
  };

  const handleChapterStatusChange = (chapterCode, status) => {
    const updated = details.map((item) => {
      if (item.chapter === chapterCode) {
        return { ...item, chapterStatus: status };
      }
      return item;
    });
    setDetails(updated);
  };

  const deleteImage = async (imgValue, index, type = "image") => {
    const updated = [...details];
    const currentImages = [...(updated[index][type] || [])];

    const confirmReplace = window.confirm(
      `If you proceed, file (${imgValue}) will be deleted.`,
    );
    if (!confirmReplace) return;

    updated[index][type] = currentImages.filter((img) => img !== imgValue);

    if (type === "image" && mode === "A") {
      const remark = JSON.parse(updated[index].remark || "{}");
      remark.image = updated[index][type];
      updated[index].remark = JSON.stringify(remark);
    }
    setDetails(updated);

    if (imgValue.startsWith("blob:") || imgValue.startsWith("http")) return;

    const folderPath = `${orgcode}/sc_${selectedDatacode}`;
    setLoading(true);
    try {
      const response = await deleteDocument({
        projectCode: "Ptw",
        envCode,
        folderPath,
        fileName: imgValue,
      });
      if (response && response.cd === 1) toast.error(response.description);
      toast.success(`Deleted from server: ${imgValue}`);
    } catch (err) {
      toast.error("Deleted locally, but server delete failed.");
      console.error("Server delete error:", err);
    } finally {
      setLoading(false);
    }
  };

  const deleteResponseImage = async (imgValue, index, type = "image", dep) => {
    const updated = [...details];
    const currentImages = [...(updated[index][type] || [])];

    const confirmReplace = window.confirm(
      `If you proceed, file (${imgValue}) will be deleted.`,
    );
    if (!confirmReplace) return;

    updated[index][type] = currentImages.filter((img) => img !== imgValue);

    if (type === "image" && mode === "A") {
      const remark = JSON.parse(updated[index].remark || "{}");
      remark.image = updated[index][type];
      updated[index].remark = JSON.stringify(remark);
    }
    setDetails(updated);

    if (imgValue.startsWith("blob:") || imgValue.startsWith("http")) return;

    const folderPath = `${orgcode}/sc_${selectedDatacode}/dep_${dep}`;
    setLoading(true);
    try {
      const response = await deleteDocument({
        projectCode: "Ptw",
        envCode,
        folderPath,
        fileName: imgValue,
      });
      if (response && response.cd === 1) toast.error(response.description);
      toast.success(`Deleted from server: ${imgValue}`);
    } catch (err) {
      toast.error("Deleted locally, but server delete failed.");
      console.error("Server delete error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (files, index, type = "image") => {
    if (!files || files.length === 0) return;

    const folderPath = `${orgcode}/sc_${selectedDatacode}`;
    const maxRetries = 3;
    const uploadedFiles = [];

    for (let file of files) {
      if (!["image/png", "image/jpeg"].includes(file.type)) {
        toast.warning(`${file.name} is not a valid JPG/PNG file.`);
        continue;
      }
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        setLoading(true);
        try {
          const uploadRes = await uploadDocument({
            file,
            projectCode: "Ptw",
            envCode,
            folderPath,
          });
          if (uploadRes?.cd === 1) {
            toast.error(uploadRes.description);
            setLoading(false);
            return;
          }
          uploadedFiles.push(uploadRes);
          break;
        } catch (err) {
          if (attempt === maxRetries)
            toast.error(`Failed to upload ${file.name}`);
        } finally {
          setLoading(false);
        }
      }
    }

    if (uploadedFiles.length) {
      const updated = [...details];
      const target = updated[index];
      const old = Array.isArray(target[type]) ? target[type] : [];
      const newList = [...old, ...uploadedFiles];
      target[type] = newList;

      if (type === "image" && mode === "A") {
        const remark = JSON.parse(target.remark || "{}");
        remark.image = newList;
        target.remark = JSON.stringify(remark);
      }
      setDetails(updated);
    }
  };

  const handleResponseImageUpload = async (
    files,
    index,
    type = "image",
    dep,
  ) => {
    if (!files || files.length === 0) return;
    if (!dep) {
      toast.warning(
        "Please select a department before uploading response images.",
      );
      return;
    }

    const folderPath = `${orgcode}/sc_${selectedDatacode}/dep_${dep}`;
    const maxRetries = 3;
    const uploadedFiles = [];

    for (let file of files) {
      if (!["image/png", "image/jpeg"].includes(file.type)) {
        toast.warning(`${file.name} is not a valid JPG/PNG file.`);
        continue;
      }
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        setLoading(true);
        try {
          const uploadRes = await uploadDocument({
            file,
            projectCode: "Ptw",
            envCode,
            folderPath,
          });
          if (uploadRes?.cd === 1) {
            toast.error(uploadRes.description);
            setLoading(false);
            return;
          }
          uploadedFiles.push(uploadRes);
          break;
        } catch (err) {
          if (attempt === maxRetries)
            toast.error(`Failed to upload ${file.name}`);
        } finally {
          setLoading(false);
        }
      }
    }

    if (uploadedFiles.length) {
      const updated = [...details];
      const target = updated[index];
      const old = Array.isArray(target[type]) ? target[type] : [];
      const newList = [...old, ...uploadedFiles];
      target[type] = newList;

      if (type === "image" && mode === "A") {
        const remark = JSON.parse(target.remark || "{}");
        remark.image = newList;
        target.remark = JSON.stringify(remark);
      }
      setDetails(updated);
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...details];
    updated[index][field] = value;

    if (mode === "A" && field === "observation") {
      const remark = JSON.parse(updated[index].remark || "{}");
      remark.observation = updated[index].observation;
      updated[index].remark = JSON.stringify(remark);
    }
    setDetails(updated);
  };

  const handleReset = (index) => {
    const original = JSON.parse(
      details[index].remark ? details[index].remark : "{}",
    );
    const updated = [...details];
    updated[index] = {
      ...updated[index],
      observation: original.observation,
      image: original.image,
      suggestion: "",
      startdate: today,
      enddate: today,
      response: "",
      responseImages: [],
    };
    setDetails(updated);
  };

  const handleDelete = (index) => {
    const updated = [...details];
    updated.splice(index, 1);
    setDetails(updated);
  };

  /* ============================================================
     RENDER — chapters as audit-style tables (4-column layout)
     ============================================================ */
  const renderGroupedByChapter = () => {
    const grouped = {};
    details.forEach((item) => {
      if (!grouped[item.chapter]) grouped[item.chapter] = [];
      grouped[item.chapter].push(item);
    });

    return Object.keys(grouped).map((chapterCode) => {
      const chapterLabel = chapterList.find((c) => c.value == chapterCode);
      const chapterRows = grouped[chapterCode];
      let qCounter = 1;

      return (
        <div
          key={chapterCode}
          ref={(el) => (chapterRefs.current[chapterCode] = el)}
          className="od-chapter"
        >
          <div className="od-chapter-header">
            {chapterLabel?.label} — {chapterLabel?.label2}
          </div>

          {chapterRows.map((item, index) => {
            const serial =
              item.parent && item.parent !== 0
                ? `${qCounter - 1}.${index + 1}`
                : `${qCounter++}`;
            const detailIdx = details.indexOf(item);
            const isModeU = mode && mode === "U";

            return (
              <table key={index} className="od-q-table">
                <tbody>
                  {/* ===== Question header (spans all 4 columns) ===== */}
                  <tr>
                    <td colSpan={4} className="od-q-header">
                      <div className="od-q-header-cell">
                        <span className="od-serial">{serial}</span>
                        <span>{item.question || "—"}</span>
                      </div>
                    </td>
                  </tr>

                  {/* ===== Row 1: Observation | AI Observation ===== */}
                  <tr>
                    <td className="od-label">Observation</td>
                    <td className="od-value">
                      <PopupInputFieldforObservationView
                        name="observation"
                        label=""
                        value={item.observation}
                        onChange={(e) =>
                          handleChange(detailIdx, "observation", e.target.value)
                        }
                      />
                    </td>
                    <td className="od-label">AI Generated Observation</td>
                    <td className="od-value">
                      <PopupInputFieldforObservationView
                        name="aiObservation"
                        label=""
                        value={item.aiObservation || ""}
                        onChange={(e) =>
                          handleChange(
                            detailIdx,
                            "aiObservation",
                            e.target.value,
                          )
                        }
                      />
                    </td>
                  </tr>

                  {/* ===== Row 2: Suggestion | Department ===== */}
                  <tr>
                    <td className="od-label">Suggestion</td>
                    <td className="od-value">
                      <PopupInputFieldforObservationView
                        name="suggestion"
                        label=""
                        value={item.suggestion}
                        onChange={(e) =>
                          handleChange(detailIdx, "suggestion", e.target.value)
                        }
                      />
                    </td>
                    <td className="od-label">Department</td>
                    <td className="od-value">
                      <Select
                        options={[
                          { label: "Please select", value: "" },
                          ...divcodeList,
                        ]}
                        value={[
                          { label: "Please select", value: "" },
                          ...divcodeList,
                        ].find((opt) => opt.value === item.department)}
                        onChange={(e) =>
                          handleChange(detailIdx, "department", e.value)
                        }
                        placeholder="Department"
                      />
                    </td>
                  </tr>

                  {/* ===== Row 3: Images (spans) ===== */}
                  <tr>
                    <td className="od-label">Images</td>
                    <td colSpan={3} className="od-value">
                      <input
                        type="file"
                        className="form-control"
                        multiple
                        accept="image/*"
                        onChange={(e) =>
                          handleImageUpload(e.target.files, detailIdx, "image")
                        }
                      />
                      {item.image && item.image.length > 0 && (
                        <div className="od-img-grid">
                          {item.image.map((img, idx2) => (
                            <div key={idx2} className="od-img-card">
                              <img
                                alt="Click to preview"
                                onClick={() => fetchAndPreviewImage(img)}
                              />
                              <button
                                type="button"
                                className="od-img-del"
                                title="Delete Image"
                                onClick={() =>
                                  deleteImage(img, detailIdx, "image")
                                }
                              >
                                ×
                              </button>
                              <span className="od-img-name" title={img}>
                                {img.split("/").pop()}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* ===== Row 4 (mode U): Annexure | Status ===== */}
                  {isModeU && (
                    <tr>
                      <td className="od-label">Annexure</td>
                      <td className="od-value">
                        <Select
                          options={[
                            { label: "Please select", value: "" },
                            ...annextureList,
                          ]}
                          value={[
                            { label: "Please select", value: "" },
                            ...annextureList,
                          ].find((opt) => opt.value === item.annexure)}
                          onChange={(e) =>
                            handleChange(detailIdx, "annexure", e.value)
                          }
                          placeholder="Annexure"
                        />
                      </td>
                      <td className="od-label">Status</td>
                      <td className="od-value">
                        <Select
                          options={[
                            { label: "Please select", value: "" },
                            ...statusOptions,
                          ]}
                          value={[
                            { label: "Please select", value: "" },
                            ...statusOptions,
                          ].find((opt) => opt.value === item.status)}
                          onChange={(e) =>
                            handleChange(detailIdx, "status", e.value)
                          }
                          placeholder="Status"
                        />
                      </td>
                    </tr>
                  )}

                  {/* ===== Row 5: Start Date | End Date (when department is set) ===== */}
                  {item.department && (
                    <tr>
                      <td className="od-label">
                        Start Date <span className="text-danger">*</span>
                      </td>
                      <td className="od-value">
                        <input
                          type="date"
                          className="form-control"
                          required
                          value={item.startdate}
                          onChange={(e) =>
                            handleChange(detailIdx, "startdate", e.target.value)
                          }
                        />
                      </td>
                      <td className="od-label">
                        End Date <span className="text-danger">*</span>
                      </td>
                      <td className="od-value">
                        <input
                          type="date"
                          className="form-control"
                          required
                          value={item.enddate}
                          onChange={(e) =>
                            handleChange(detailIdx, "enddate", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  )}

                  {/* ===== Row 6 (mode U): Response (spans) ===== */}
                  {isModeU && (
                    <tr>
                      <td className="od-label">Response</td>
                      <td colSpan={3} className="od-value">
                        <PopupInputFieldforObservationView
                          name="response"
                          label=""
                          value={item.response || ""}
                          onChange={(e) =>
                            handleChange(detailIdx, "response", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  )}

                  {/* ===== Row 7 (mode U): Response Images (spans) ===== */}
                  {isModeU && (
                    <tr>
                      <td className="od-label">Response Images</td>
                      <td colSpan={3} className="od-value">
                        <input
                          type="file"
                          className="form-control"
                          multiple
                          accept="image/*"
                          onChange={(e) =>
                            handleResponseImageUpload(
                              e.target.files,
                              detailIdx,
                              "responseImages",
                              item.department,
                            )
                          }
                        />
                        {item.responseImages &&
                          item.responseImages.length > 0 && (
                            <div className="od-img-grid">
                              {item.responseImages.map((img, idx2) => (
                                <div key={idx2} className="od-img-card">
                                  <img
                                    alt="Click to preview"
                                    onClick={() =>
                                      fetchAndPreviewResponseImage(
                                        img,
                                        item.department,
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    className="od-img-del"
                                    title="Delete Image"
                                    onClick={() =>
                                      deleteResponseImage(
                                        img,
                                        detailIdx,
                                        "responseImages",
                                        item.department,
                                      )
                                    }
                                  >
                                    ×
                                  </button>
                                  <span className="od-img-name" title={img}>
                                    {img.split("/").pop()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            );
          })}
        </div>
      );
    });
  };

  return (
    <>
      <style>{odStyles}</style>
      <div className="od-wrap">
        {loading && <CustomLoader />}

        {/* ---- Toggle button ---- */}
        {!showChapters && (
          <button
            type="button"
            className="od-toggle od-toggle--open"
            onClick={() => setShowChapters(true)}
          >
            {details && details.length > 0
              ? "View Inspection Report"
              : "Add Inspection Report"}
          </button>
        )}
        {showChapters && (
          <button
            type="button"
            className="od-toggle od-toggle--close"
            onClick={() => setShowChapters(false)}
          >
            Close Inspection
          </button>
        )}

        {showChapters && <>{renderGroupedByChapter()}</>}

        {/* ---- Fullscreen preview overlay ---- */}
        {previewImg && (
          <div
            className="od-preview-overlay"
            onClick={() => setPreviewImg(null)}
          >
            <img src={previewImg} alt="Full Preview" />
          </div>
        )}

        {/* ---- Floating chapter-menu button ---- */}
        {!showChapterMenu && (
          <button
            type="button"
            className="od-fab"
            onClick={() => setShowChapterMenu(true)}
            title="Chapters"
          >
            <FaListUl />
          </button>
        )}

        {/* ---- Sliding Chapter Drawer ---- */}
        <div
          className="od-drawer"
          style={{ right: showChapterMenu ? "0" : "-280px" }}
        >
          <div className="od-drawer-head">
            <span>Chapters</span>
            <FaTimes onClick={() => setShowChapterMenu(false)} />
          </div>

          <div className="od-drawer-search">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search chapters..."
            />
          </div>

          <div className="od-drawer-list">
            {filteredChapters.length > 0 ? (
              filteredChapters.map((chapter) => (
                <div
                  key={chapter.value}
                  className="od-drawer-item"
                  onClick={() => {
                    const exists = details.some(
                      (d) => d.chapter === chapter.value,
                    );
                    if (exists && chapterRefs.current[chapter.value]) {
                      chapterRefs.current[chapter.value].scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    } else {
                      toast.warning("No such chapter..!");
                    }
                  }}
                >
                  {chapter.label} — {chapter.label2}
                </div>
              ))
            ) : (
              <div className="od-drawer-empty">No chapters found</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ObservationDetails;
